import type { AxiosRequestConfig } from "axios";
import { ApiService } from "./api.service";
import { User, UserDocument, UserProfileAdminResponse } from "../types/user";
import { Organization, OrganizationDocument } from "../types/organization";
import { adminApiClient, projectsApiClient } from "./api-client.service";
import { EWalletDocument } from "../types/eWallet";
import { TaskComment, ProjectFeedback } from "../types/task";
import {
  OrganizationRequestDetail,
  OrganizationRequestListResponse,
} from "../types/organization-request";

export interface AdminProjectFeedbackListItem {
  id: string;
  projectId: string;
  projectName: string;
  organizationId: string;
  organizationName?: string | null;
  volunteerUserId: string;
  volunteerName: string;
  coordinatorUserId?: string | null;
  coordinatorName?: string | null;
  submittedAt: string;
  updatedAt?: string | null;
  updatedByUserId?: string | null;
  updatedByName?: string | null;
  bodyPreview: string;
}

export interface AdminProjectFeedbackListResponse {
  feedback: AdminProjectFeedbackListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AdminProjectFeedbackDetail extends ProjectFeedback {
  projectName: string;
  coordinatorUserId?: string | null;
  coordinatorName?: string | null;
}

export interface AdminTaskCommentListItem {
  id: string;
  taskId: string;
  taskName: string | null;
  projectId: string;
  projectName: string | null;
  organizationId: string | null;
  organizationName: string | null;
  postedByUserId: string;
  postedByName: string | null;
  postedByRoleBadge: string;
  bodyPreview: string;
  createdAt: string;
  isTaskDeleted?: boolean;
  isProjectDeleted?: boolean;
  isCommentDeleted?: boolean;
}

export interface AdminTaskCommentListResponse {
  comments: AdminTaskCommentListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AdminTaskCommentDetail extends TaskComment {
  taskName?: string | null;
  projectName?: string | null;
  projectOrganizationId?: string | null;
  projectOrganizationName?: string | null;
  organizationName?: string | null;
  isDeleted?: boolean;
  isTaskDeleted?: boolean;
  isProjectDeleted?: boolean;
  taskDeletedAt?: string | null;
  projectDeletedAt?: string | null;
  deletedAt?: string | null;
  deletedBy?: string | null;
}

class AdminService extends ApiService {
  async getSuperAdminDashboardOverview(): Promise<{
    totalUsers: {
      total: number;
      volunteers: number;
      coordinators: number;
      orgAdmins: number;
      superAdmins: number;
      change: number;
    };
    ngosRegistered: {
      total: number;
      change: number;
    };
    activeProjects: {
      total: number;
      change: number;
    };
    totalTasks: {
      total: number;
      change: number;
    };
  }> {
    return this.get(adminApiClient, "/dashboard/super-admin/overview");
  }

  async getOrgAdminDashboardOverview(): Promise<{
    totalUsers: {
      total: number;
      volunteers: number;
      coordinators: number;
      orgAdmins: number;
    };
    activeProjects: {
      total: number;
    };
    totalTasks: {
      total: number;
    };
    activeVolunteers: {
      total: number;
      withAssignedTasks: number;
      withNoTasks: number;
      withActiveTasks: number;
      withAssignedTasksVolunteers: Array<{
        id: string;
        firstName: string | null;
        lastName: string | null;
      }>;
      withNoTasksVolunteers: Array<{
        id: string;
        firstName: string | null;
        lastName: string | null;
      }>;
      withActiveTasksVolunteers: Array<{
        id: string;
        firstName: string | null;
        lastName: string | null;
      }>;
    };
  }> {
    return this.get(adminApiClient, "/dashboard/org-admin/overview");
  }

  async getUsers(params?: {
    search?: string;
    organizationId?: string;
    role?: string;
    status?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    ids?: string[];
  }): Promise<{
    users: User[];
    pagination: {
      total: number;
      totalPages: number;
      page: number;
      limit: number;
    };
  }> {
    return await this.get<{ users: User[]; pagination: any }>(
      adminApiClient,
      "/users",
      params,
    );
  }

  async getUsersByIds(ids: string[], silent = false): Promise<User[]> {
    const unique = Array.from(new Set(ids.map((v) => v?.trim?.() ?? v))).filter(
      (v) => !!v && v.trim().length > 0,
    );
    if (unique.length === 0) return [];
    // Axios default array serialization may send `ids[]=...`, while the Admin API expects `ids=...`.
    // Build the query string explicitly to guarantee `ids` repeats as expected.
    const qs = new URLSearchParams();
    unique.slice(0, 200).forEach((id) => qs.append("ids", id));
    qs.set("page", "1");
    qs.set("limit", String(Math.min(unique.length, 200)));

    const resp = await this.get<{ users: User[]; pagination: any }>(
      adminApiClient,
      `/users?${qs.toString()}`,
      undefined,
      silent,
    );
    return resp.users ?? [];
  }

  async getUserById(
    userId: string,
    silent = false,
    includeSoftDeleted = false,
  ): Promise<User> {
    return this.get<User>(
      adminApiClient,
      `/users/${userId}`,
      includeSoftDeleted ? { includeSoftDeleted: "true" } : undefined,
      silent,
    );
  }

  async checkUserExists(email: string): Promise<{
    exists: boolean;
    inOrganization: boolean;
    userId?: string;
    isSuperAdmin?: boolean;
  }> {
    return this.get<{
      exists: boolean;
      inOrganization: boolean;
      userId?: string;
      isSuperAdmin?: boolean;
    }>(adminApiClient, `/users/check-email/${encodeURIComponent(email)}`);
  }

  async createUser(data: {
    email: string;
    role: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    dateOfBirth?: string | null;
    addressOfLiving?: string | null;
    organizations?: Array<{
      organizationId: string;
      role: string;
    }>;
  }): Promise<User & { existingUser?: boolean }> {
    return this.post<User & { existingUser?: boolean }>(
      adminApiClient,
      "/users",
      data,
    );
  }

  async updateUser(
    userId: string,
    data: {
      email?: string;
      role?: string;
      password?: string;
      organizations?: Array<{
        organizationId: string;
        role: string;
      }>;
    },
  ): Promise<User> {
    return this.patch<User>(adminApiClient, `/users/${userId}`, data);
  }

  async updateUserProfile(
    userId: string,
    data: {
      firstName?: string;
      lastName?: string;
      phone?: string;
      dateOfBirth?: string | null;
      addressOfLiving?: string | null;
      status?: string;
      avatar?: string;
      organizations?: Array<{
        organizationId: string;
        role: string;
      }>;
    },
  ): Promise<User> {
    return this.patch<User>(adminApiClient, `/users/${userId}`, data);
  }

  async deleteUser(userId: string): Promise<{ message: string }> {
    // Skip global error toast because callers surface validation errors themselves
    return this.delete<{ message: string }>(
      adminApiClient,
      `/users/${userId}`,
      true,
    );
  }

  async resendUserInvitation(userId: string): Promise<{ message: string }> {
    return this.post<{ message: string }>(
      adminApiClient,
      `/users/${userId}/resend-invitation`,
      {},
    );
  }

  async resetUserPassword(userId: string): Promise<{ message: string }> {
    return this.post<{ message: string }>(
      adminApiClient,
      `/users/${userId}/reset-password`,
      {},
    );
  }

  async getUserStatistics(userId: string): Promise<{
    assignedProjects: number;
    completedTasks: number;
    deletedTasks: number;
    totalHours: number;
  }> {
    return this.get<{
      assignedProjects: number;
      completedTasks: number;
      deletedTasks: number;
      totalHours: number;
    }>(adminApiClient, `/users/${userId}/statistics`);
  }

  async uploadUserAvatar(
    userId: string,
    file: File,
    onUploadProgress?: (progressEvent: {
      loaded: number;
      total: number;
    }) => void,
  ): Promise<{ avatar: string }> {
    try {
      const formData = new FormData();
      formData.append("avatar", file);

      const response = await adminApiClient.post<{ avatar: string }>(
        `/users/${userId}/avatar`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: onUploadProgress
            ? (progressEvent: any) => {
                if (progressEvent.total) {
                  onUploadProgress({
                    loaded: progressEvent.loaded,
                    total: progressEvent.total,
                  });
                }
              }
            : undefined,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async removeUserAvatar(userId: string): Promise<void> {
    await adminApiClient.delete(`/users/${userId}/avatar`);
  }

  async getOrganizations(params: {
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    ids?: string[];
    country?: string;
  }): Promise<{ organizations: Organization[]; pagination: any }> {
    return this.get<{ organizations: Organization[]; pagination: any }>(
      adminApiClient,
      "/organizations",
      params,
    );
  }

  async getOrganizationCountries(): Promise<string[]> {
    const response = await this.get<{ countries: string[] }>(
      adminApiClient,
      "/organizations/countries",
    );
    return response?.countries ?? [];
  }

  async getOrganizationsByIds(
    ids: string[],
    silent = false,
  ): Promise<Organization[]> {
    const unique = Array.from(new Set(ids)).filter((v) => !!v && v.trim().length > 0);
    if (unique.length === 0) return [];
    const qs = new URLSearchParams();
    unique
      .map((v) => v.trim())
      .filter((v) => v.length > 0)
      .slice(0, 200)
      .forEach((id) => qs.append("ids", id));
    qs.set("page", "1");
    qs.set("limit", String(Math.min(unique.length, 200)));

    return this.get<{ organizations: Organization[]; pagination: any }>(
      adminApiClient,
      `/organizations?${qs.toString()}`,
      undefined,
      silent,
    ).then((r) => r.organizations ?? []);
  }

  async getOrganizationById(orgId: string): Promise<Organization> {
    return this.get<Organization>(adminApiClient, `/organizations/${orgId}`);
  }

  async removeMemberFromOrganization(
    organizationId: string,
    userId: string,
    role?: string,
  ): Promise<{ message: string }> {
    const url =
      role != null
        ? `/organizations/${organizationId}/members/${userId}?role=${encodeURIComponent(role)}`
        : `/organizations/${organizationId}/members/${userId}`;
    return this.delete<{ message: string }>(adminApiClient, url);
  }

  async getCurrentUserProfile(): Promise<UserProfileAdminResponse> {
    return this.get<UserProfileAdminResponse>(adminApiClient, "/users/profile");
  }

  async acceptTermsOfUse(): Promise<{ termsOfUseAccepted: boolean }> {
    return this.post<{ termsOfUseAccepted: boolean }>(
      adminApiClient,
      "/users/terms-of-use/accept",
      { accepted: true },
    );
  }

  async createOrganization(data: {
    name: string;
    slug: string;
    description?: string;
    category: string;
    oid: string;
    pif?: string;
    address: string;
    country: string;
    website?: string;
    email: string;
    phone: string;
    president: string;
    qualityLabel?: string;
    accreditationNumber?: string;
    escVolunteersHostingShortTerm?: number;
    escVolunteersHostingLongTerm?: number;
    escVolunteersSendingShortTerm?: number;
    escVolunteersSendingLongTerm?: number;
    escVolunteersCoordinatingShortTerm?: number;
    escVolunteersCoordinatingLongTerm?: number;
    hostingVolunteeringTeams?: number;
    sendingVolunteeringTeams?: number;
    otherVolunteeringPrograms?: string;
  }): Promise<Organization> {
    return this.post<Organization>(adminApiClient, "/organizations", data);
  }

  async getOrganizationRequests(params?: {
    search?: string;
    status?: "PENDING" | "REJECTED" | "APPROVED";
    sortBy?: "organizationName" | "submittedAt";
    sortOrder?: "asc" | "desc";
    page?: number;
    limit?: number;
  }): Promise<OrganizationRequestListResponse> {
    return this.get<OrganizationRequestListResponse>(
      adminApiClient,
      "/organization-requests",
      params,
    );
  }

  async getOrganizationRequestById(id: string): Promise<OrganizationRequestDetail> {
    return this.get<OrganizationRequestDetail>(
      adminApiClient,
      `/organization-requests/${id}`,
    );
  }

  async rejectOrganizationRequest(
    id: string,
    payload?: {
      reason?: string;
      reviewData?: Partial<OrganizationRequestDetail>;
    },
  ): Promise<{ id: string; status: "REJECTED" }> {
    return this.patch<{ id: string; status: "REJECTED" }>(
      adminApiClient,
      `/organization-requests/${id}/reject`,
      payload ?? {},
    );
  }

  async approveOrganizationRequest(
    id: string,
    reviewData?: Partial<OrganizationRequestDetail>,
  ): Promise<{
    requestId: string;
    status: "APPROVED";
    organizationId: string;
    representativeUserId?: string;
  }> {
    return this.patch(
      adminApiClient,
      `/organization-requests/${id}/approve`,
      { reviewData },
    );
  }

  async subscribePublicNewsletter(payload: {
    email: string;
    captchaToken?: string;
  }): Promise<{ message: string }> {
    const config = {
      __skipGlobalErrorToast: true,
    } as AxiosRequestConfig & { __skipGlobalErrorToast?: boolean };
    const response = await adminApiClient.post<{ message: string }>(
      "/public/newsletter-subscribe",
      {
        email: payload.email.trim(),
        ...(payload.captchaToken
          ? { captchaToken: payload.captchaToken }
          : {}),
      },
      config,
    );
    return response.data;
  }

  async submitPublicOrganizationRequest(payload: {
    organizationName: string;
    category: string;
    oid: string;
    address: string;
    country: string;
    organizationEmail: string;
    president: string;
    representativeFirstName: string;
    representativeLastName: string;
    representativeEmail: string;
    representativePhone: string;
    message?: string;
    consentAccepted: boolean;
    captchaToken?: string;
  }): Promise<{ id: string; message: string }> {
    // Public endpoint intentionally works without auth token
    return this.post(adminApiClient, "/public/organization-requests", payload);
  }

  async updateOrganization(
    orgId: string,
    data: {
      name?: string;
      /** Super Admins only - the API rejects a changed slug from any other role. */
      slug?: string;
      description?: string | null;
      category?: string;
      oid?: string;
      pif?: string | null;
      address?: string;
      country?: string;
      website?: string | null;
      email?: string;
      phone?: string;
      president?: string;
      qualityLabel?: string | null;
      accreditationNumber?: string | null;
      escVolunteersHostingShortTerm?: number | null;
      escVolunteersHostingLongTerm?: number | null;
      escVolunteersSendingShortTerm?: number | null;
      escVolunteersSendingLongTerm?: number | null;
      escVolunteersCoordinatingShortTerm?: number | null;
      escVolunteersCoordinatingLongTerm?: number | null;
      hostingVolunteeringTeams?: number | null;
      sendingVolunteeringTeams?: number | null;
      otherVolunteeringPrograms?: string | null;
    },
  ): Promise<Organization> {
    return this.patch<Organization>(
      adminApiClient,
      `/organizations/${orgId}`,
      data,
    );
  }

  async deleteOrganization(orgId: string): Promise<{ message: string }> {
    // Skip global error toast because callers surface validation errors themselves
    return this.delete<{ message: string }>(
      adminApiClient,
      `/organizations/${orgId}`,
      true,
    );
  }

  async uploadOrganizationDocument(
    orgId: string,
    file: File,
    metadata: {
      name: string;
      type: "STATUTE" | "POLICY" | "LEGAL_DOCUMENT" | "OTHER";
      description?: string;
    },
    onUploadProgress?: (progressEvent: {
      loaded: number;
      total: number;
    }) => void,
  ): Promise<OrganizationDocument> {
    try {
      const formData = new FormData();
      // Append fields before file so multipart parsers receive name/type/description reliably
      formData.append("name", metadata.name);
      formData.append("type", metadata.type);
      if (metadata.description) {
        formData.append("description", metadata.description);
      }
      formData.append("file", file);

      const response = await adminApiClient.post<OrganizationDocument>(
        `/organizations/${orgId}/documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: onUploadProgress
            ? (progressEvent: any) => {
                if (progressEvent.total) {
                  onUploadProgress({
                    loaded: progressEvent.loaded,
                    total: progressEvent.total,
                  });
                }
              }
            : undefined,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getOrganizationDocuments(
    orgId: string,
    params?: {
      page?: number;
      limit?: number;
      type?: "STATUTE" | "POLICY" | "LEGAL_DOCUMENT" | "OTHER";
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    },
  ): Promise<{
    documents: OrganizationDocument[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    return this.get<{
      documents: OrganizationDocument[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(adminApiClient, `/organizations/${orgId}/documents`, params);
  }

  async downloadOrganizationDocument(
    orgId: string,
    docId: string,
  ): Promise<{ downloadUrl: string }> {
    return this.get<{ downloadUrl: string }>(
      adminApiClient,
      `/organizations/${orgId}/documents/${docId}/download`,
      undefined,
    );
  }

  async updateOrganizationDocument(
    orgId: string,
    docId: string,
    data: {
      name?: string;
      type?: "STATUTE" | "POLICY" | "LEGAL_DOCUMENT" | "OTHER";
      description?: string;
    },
  ): Promise<OrganizationDocument> {
    return this.patch<OrganizationDocument>(
      adminApiClient,
      `/organizations/${orgId}/documents/${docId}`,
      data,
    );
  }

  async deleteOrganizationDocument(
    orgId: string,
    docId: string,
  ): Promise<void> {
    await this.delete<void>(
      adminApiClient,
      `/organizations/${orgId}/documents/${docId}`,
    );
  }

  async uploadUserDocument(
    userId: string,
    file: File,
    metadata: {
      name: string;
      type:
        | "RESUME"
        | "CERTIFICATE"
        | "ID_DOCUMENT"
        | "CONTRACT"
        | "INSURANCE"
        | "TRAINING_RECORD"
        | "BACKGROUND_CHECK"
        | "WORK_PERMIT"
        | "OTHER";
      description?: string;
    },
    onUploadProgress?: (progressEvent: {
      loaded: number;
      total: number;
    }) => void,
  ): Promise<UserDocument> {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", metadata.name);
      formData.append("type", metadata.type);
      if (metadata.description) {
        formData.append("description", metadata.description);
      }

      const response = await adminApiClient.post<UserDocument>(
        `/users/${userId}/documents`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: onUploadProgress
            ? (progressEvent: any) => {
                if (progressEvent.total) {
                  onUploadProgress({
                    loaded: progressEvent.loaded,
                    total: progressEvent.total,
                  });
                }
              }
            : undefined,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getUserDocuments(
    userId: string,
    params?: {
      page?: number;
      limit?: number;
      type?: "RESUME" | "CERTIFICATE" | "ID_DOCUMENT" | "OTHER";
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    },
  ): Promise<{
    documents: UserDocument[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    return this.get<{
      documents: UserDocument[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(adminApiClient, `/users/${userId}/documents`, params, true);
  }

  async downloadUserDocument(
    userId: string,
    docId: string,
  ): Promise<{ downloadUrl: string }> {
    return this.get<{ downloadUrl: string }>(
      adminApiClient,
      `/users/${userId}/documents/${docId}/download`,
      undefined,
    );
  }

  async updateUserDocument(
    userId: string,
    docId: string,
    data: {
      name?: string;
      type?:
        | "RESUME"
        | "CERTIFICATE"
        | "ID_DOCUMENT"
        | "CONTRACT"
        | "INSURANCE"
        | "TRAINING_RECORD"
        | "BACKGROUND_CHECK"
        | "WORK_PERMIT"
        | "OTHER";
      description?: string;
    },
  ): Promise<UserDocument> {
    return this.patch<UserDocument>(
      adminApiClient,
      `/users/${userId}/documents/${docId}`,
      data,
    );
  }

  async deleteUserDocument(userId: string, docId: string): Promise<void> {
    await this.delete<void>(
      adminApiClient,
      `/users/${userId}/documents/${docId}`,
    );
  }

  async getEWalletCoordinators(userId: string): Promise<
    Array<{
      id: string;
      firstName: string | null;
      lastName: string | null;
      email: string;
      phone: string | null;
      assignmentStartDate: string;
      assignmentEndDate: string;
      projectId: string;
    }>
  > {
    return this.get(
      adminApiClient,
      `/users/${userId}/e-wallet/coordinators`,
      undefined,
    );
  }

  async getEWalletProjectInfo(userId: string): Promise<{
    projectId: string | null;
    leadOrganization: string | null;
    hostingOrganization: string | null;
    sendingOrganization: string | null;
    locationOfActivity: string | null;
    activityStartDate: string | null;
    activityEndDate: string | null;
    budget: number | null;
  }> {
    return this.get(
      adminApiClient,
      `/users/${userId}/e-wallet/project-information`,
      undefined,
    );
  }

  async updateEWalletProjectInfo(
    userId: string,
    data: {
      projectId?: string | null;
      leadOrganization?: string | null;
      hostingOrganization?: string | null;
      sendingOrganization?: string | null;
      locationOfActivity?: string | null;
      activityStartDate?: string | null;
      activityEndDate?: string | null;
      budget?: number | null;
    },
  ): Promise<{
    projectId: string | null;
    leadOrganization: string | null;
    hostingOrganization: string | null;
    sendingOrganization: string | null;
    locationOfActivity: string | null;
    activityStartDate: string | null;
    activityEndDate: string | null;
    budget: number | null;
  }> {
    return this.put(
      adminApiClient,
      `/users/${userId}/e-wallet/project-information`,
      data,
    );
  }

  async getEWalletEmergencyContacts(userId: string): Promise<
    Array<{
      id: string;
      contactName: string;
      email: string | null;
      phone: string;
    }>
  > {
    return this.get(
      adminApiClient,
      `/users/${userId}/e-wallet/emergency-contacts`,
      undefined,
    );
  }

  async addEWalletEmergencyContact(
    userId: string,
    data: { contactName: string; email?: string | null; phone: string },
  ): Promise<{
    id: string;
    contactName: string;
    email: string | null;
    phone: string;
  }> {
    return this.post(
      adminApiClient,
      `/users/${userId}/e-wallet/emergency-contacts`,
      data,
    );
  }

  async updateEWalletEmergencyContact(
    userId: string,
    contactId: string,
    data: { contactName?: string; email?: string | null; phone?: string },
  ): Promise<{
    id: string;
    contactName: string;
    email: string | null;
    phone: string;
  }> {
    return this.patch(
      adminApiClient,
      `/users/${userId}/e-wallet/emergency-contacts/${contactId}`,
      data,
    );
  }

  async deleteEWalletEmergencyContact(
    userId: string,
    contactId: string,
  ): Promise<void> {
    await this.delete(
      adminApiClient,
      `/users/${userId}/e-wallet/emergency-contacts/${contactId}`,
    );
  }

  async getEWalletDocuments(
    userId: string,
    params?: {
      page?: number;
      limit?: number;
      type?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    },
  ): Promise<{
    documents: EWalletDocument[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  }> {
    return this.get(
      adminApiClient,
      `/users/${userId}/e-wallet/documents`,
      params,
    );
  }

  async uploadEWalletDocument(
    userId: string,
    file: File,
    metadata: { name: string; type: string; description?: string },
    onUploadProgress?: (e: { loaded: number; total: number }) => void,
  ): Promise<EWalletDocument> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", metadata.name);
    formData.append("type", metadata.type);
    if (metadata.description)
      formData.append("description", metadata.description);

    const response = await adminApiClient.post(
      `/users/${userId}/e-wallet/documents`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onUploadProgress
          ? (e) =>
              onUploadProgress({ loaded: e.loaded ?? 0, total: e.total ?? 0 })
          : undefined,
      },
    );
    return response.data;
  }

  async downloadEWalletDocument(
    userId: string,
    docId: string,
  ): Promise<{ downloadUrl: string }> {
    return this.get(
      adminApiClient,
      `/users/${userId}/e-wallet/documents/${docId}/download`,
      undefined,
    );
  }

  async updateEWalletDocument(
    userId: string,
    docId: string,
    data: { name?: string; type?: string; description?: string },
  ): Promise<EWalletDocument> {
    return this.patch(
      adminApiClient,
      `/users/${userId}/e-wallet/documents/${docId}`,
      data,
    );
  }

  async deleteEWalletDocument(userId: string, docId: string): Promise<void> {
    await this.delete<void>(
      adminApiClient,
      `/users/${userId}/e-wallet/documents/${docId}`,
    );
  }

  async getTaskComments(
    taskId: string,
    params?: { limit?: number; cursor?: string },
  ): Promise<{
    comments: TaskComment[];
    total: number;
    hasMore: boolean;
    nextCursor?: string | null;
    canComment: boolean;
  }> {
    type ApiCommentRow = {
      id: string;
      taskId: string;
      projectId: string;
      projectAssignmentId: string | null;
      authorUserId: string;
      authorName: string;
      authorRole: string;
      authorRoleLabel: string;
      avatarUrl?: string | null;
      body: string;
      isEdited: boolean;
      createdAt: string;
      updatedAt: string | null;
      canEdit: boolean;
      canDelete: boolean;
    };

    type ApiListPayload = {
      comments: ApiCommentRow[];
      total: number;
      hasMore: boolean;
      nextCursor: string | null;
      canComment: boolean;
    };

    const query: Record<string, string | number> = {};
    if (params?.limit != null) {
      query.limit = params.limit;
    }
    if (params?.cursor) {
      query.before = params.cursor;
    }

    const raw = await this.get<ApiListPayload | ApiCommentRow[]>(
      projectsApiClient,
      `/tasks/${taskId}/comments`,
      query,
    );

    const payload: ApiListPayload = Array.isArray(raw)
      ? {
          comments: raw,
          total: raw.length,
          hasMore: false,
          nextCursor: null,
          canComment: true,
        }
      : raw;

    const comments: TaskComment[] = payload.comments.map((c) => ({
      id: c.id,
      taskId: c.taskId,
      projectId: c.projectId,
      organizationId: "", // not provided by API; unused in UI for now
      authorUserId: c.authorUserId,
      authorName: c.authorName,
      authorDisplayName: c.authorName,
      authorInitials: undefined,
      authorAvatarUrl: c.avatarUrl ?? null,
      authorRole: c.authorRole,
      authorRoleBadge: c.authorRoleLabel,
      authorDeleted: false,
      body: c.body,
      isEdited: c.isEdited,
      createdAt: c.createdAt,
      updatedAt: c.updatedAt,
      deletedAt: undefined,
      canEdit: c.canEdit,
      canDelete: c.canDelete,
    }));

    return {
      comments,
      total: payload.total,
      hasMore: payload.hasMore,
      nextCursor: payload.nextCursor ?? null,
      canComment: payload.canComment,
    };
  }

  async addTaskComment(
    taskId: string,
    data: { body: string },
  ): Promise<TaskComment> {
    const created = await this.post<{
      id: string;
      taskId: string;
      projectId: string;
      projectAssignmentId: string | null;
      authorUserId: string;
      authorName: string;
      authorRole: string;
      authorRoleLabel: string;
      avatarUrl?: string | null;
      body: string;
      isEdited: boolean;
      createdAt: string;
      updatedAt: string | null;
      canEdit: boolean;
      canDelete: boolean;
    }>(projectsApiClient, `/tasks/${taskId}/comments`, data);

    return {
      id: created.id,
      taskId: created.taskId,
      projectId: created.projectId,
      organizationId: "",
      authorUserId: created.authorUserId,
      authorName: created.authorName,
      authorDisplayName: created.authorName,
      authorInitials: undefined,
      authorAvatarUrl: created.avatarUrl ?? null,
      authorRole: created.authorRole,
      authorRoleBadge: created.authorRoleLabel,
      authorDeleted: false,
      body: created.body,
      isEdited: created.isEdited,
      createdAt: created.createdAt,
      updatedAt: created.updatedAt,
      deletedAt: undefined,
      canEdit: created.canEdit,
      canDelete: created.canDelete,
    };
  }

  async updateTaskComment(
    taskId: string,
    commentId: string,
    data: { body: string },
  ): Promise<TaskComment> {
    const updated = await this.patch<{
      id: string;
      taskId: string;
      projectId: string;
      projectAssignmentId: string | null;
      authorUserId: string;
      authorName: string;
      authorRole: string;
      authorRoleLabel: string;
      avatarUrl?: string | null;
      body: string;
      isEdited: boolean;
      createdAt: string;
      updatedAt: string | null;
      canEdit: boolean;
      canDelete: boolean;
    }>(projectsApiClient, `/tasks/${taskId}/comments/${commentId}`, data);

    return {
      id: updated.id,
      taskId: updated.taskId,
      projectId: updated.projectId,
      organizationId: "",
      authorUserId: updated.authorUserId,
      authorName: updated.authorName,
      authorDisplayName: updated.authorName,
      authorInitials: undefined,
      authorAvatarUrl: updated.avatarUrl ?? null,
      authorRole: updated.authorRole,
      authorRoleBadge: updated.authorRoleLabel,
      authorDeleted: false,
      body: updated.body,
      isEdited: updated.isEdited,
      createdAt: updated.createdAt,
      updatedAt: updated.updatedAt,
      deletedAt: undefined,
      canEdit: updated.canEdit,
      canDelete: updated.canDelete,
    };
  }

  async deleteTaskComment(taskId: string, commentId: string): Promise<void> {
    await this.delete<void>(
      projectsApiClient,
      `/tasks/${taskId}/comments/${commentId}`,
    );
  }

  async getAdminProjectFeedbackList(params: {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    volunteerId?: string;
    coordinatorId?: string;
    projectId?: string;
    organizationId?: string;
  }): Promise<AdminProjectFeedbackListResponse> {
    const response = await this.get<{
      items: Array<{
        id: string;
        projectId: string;
        projectName: string | null;
        volunteerId: string;
        volunteerName: string | null;
        coordinatorId: string;
        coordinatorName: string | null;
        organizationId: string;
        organizationName: string | null;
        submittedAt: string;
        lastUpdatedAt: string | null;
        updatedByUserId: string | null;
        updatedByName: string | null;
        feedbackPreview: string;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(projectsApiClient, "/admin/feedback/project", params);

    const feedback: AdminProjectFeedbackListItem[] = response.items.map(
      (item) => ({
        id: item.id,
        projectId: item.projectId,
        projectName: item.projectName ?? "",
        organizationId: item.organizationId,
        organizationName: item.organizationName,
        volunteerUserId: item.volunteerId,
        volunteerName: item.volunteerName ?? "",
        coordinatorUserId: item.coordinatorId,
        coordinatorName: item.coordinatorName,
        submittedAt: item.submittedAt,
        updatedAt: item.lastUpdatedAt,
        updatedByUserId: item.updatedByUserId ?? undefined,
        updatedByName: item.updatedByName ?? undefined,
        bodyPreview: item.feedbackPreview,
      }),
    );

    return {
      feedback,
      pagination: response.pagination,
    };
  }

  async getAdminProjectFeedbackDetail(
    id: string,
  ): Promise<AdminProjectFeedbackDetail> {
    const response = await this.get<{
      id: string;
      projectId: string;
      projectName: string | null;
      volunteerId: string;
      volunteerName: string | null;
      coordinatorId: string | null;
      coordinatorName: string | null;
      projectAssignmentId?: string;
      organizationId: string;
      organizationName: string | null;
      feedback: string;
      submittedAt: string;
      updatedAt: string | null;
      updatedBy: string | null;
      updatedByName: string | null;
      canEdit: boolean;
      canDelete: boolean;
    }>(projectsApiClient, `/admin/feedback/project/${id}`);

    return {
      id: response.id,
      projectId: response.projectId,
      projectName: response.projectName ?? "",
      assignmentId: response.projectAssignmentId ?? undefined,
      organizationId: response.organizationId,
      volunteerUserId: response.volunteerId,
      volunteerName: response.volunteerName ?? "",
      body: response.feedback ?? "",
      submittedAt: response.submittedAt,
      updatedAt: response.updatedAt,
      updatedByUserId: response.updatedBy ?? null,
      updatedByName: response.updatedByName ?? null,
      canEdit: !!response.canEdit,
      canDelete: !!response.canDelete,
      coordinatorUserId: response.coordinatorId ?? null,
      coordinatorName: response.coordinatorName ?? null,
    };
  }

  async getAdminTaskCommentsList(params: {
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortDir?: "asc" | "desc";
    postedByUserId?: string;
    projectId?: string;
    organizationId?: string;
  }): Promise<AdminTaskCommentListResponse> {
    const response = await this.get<{
      items: Array<{
        id: string;
        taskId: string;
        taskName: string | null;
        projectId: string;
        projectName: string | null;
        organizationId: string;
        organizationName: string | null;
        authorUserId: string;
        authorName: string | null;
        authorRole: string;
        authorRoleLabel: string;
        commentPreview: string;
        postedAt: string;
        isTaskDeleted?: boolean;
        isProjectDeleted?: boolean;
        isCommentDeleted?: boolean;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>(projectsApiClient, "/admin/feedback/task-comments", params);

    const comments: AdminTaskCommentListItem[] = response.items.map(
      (item) => ({
        id: item.id,
        taskId: item.taskId,
        taskName: item.taskName,
        projectId: item.projectId,
        projectName: item.projectName,
        organizationId: item.organizationId ?? null,
        organizationName: item.organizationName,
        postedByUserId: item.authorUserId,
        postedByName: item.authorName,
        postedByRoleBadge: item.authorRoleLabel,
        bodyPreview: item.commentPreview,
        createdAt: item.postedAt,
        isTaskDeleted: item.isTaskDeleted,
        isProjectDeleted: item.isProjectDeleted,
        isCommentDeleted: item.isCommentDeleted,
      }),
    );

    return {
      comments,
      pagination: response.pagination,
    };
  }

  async getAdminTaskCommentDetail(id: string): Promise<AdminTaskCommentDetail> {
    return this.get<AdminTaskCommentDetail>(
      projectsApiClient,
      `/admin/feedback/task-comments/${id}`,
    );
  }

  async chatbotQuery(payload: {
    message: string;
    sessionId?: string;
    history?: Array<{ role: "user" | "assistant"; content: string }>;
    organizationId?: string;
    publicSessionId?: string;
    captchaToken?: string;
  }): Promise<{
    sessionId: string;
    messageId: string;
    answer: string;
    citations: Array<{ source: string; title?: string; snippet?: string }>;
    latencyMs: number;
    outOfScope?: boolean;
  }> {
    return this.post(adminApiClient, "/chatbot/query", payload);
  }

  async chatbotMessage(payload: {
    message: string;
    sessionId?: string;
    captchaToken?: string;
  }): Promise<{
    sessionId: string;
    messageId: string;
    message: string;
  }> {
    return this.post(adminApiClient, "/chatbot/message", payload);
  }

  async chatbotFeedback(payload: {
    sessionId: string;
    messageId: string;
    feedback: "positive" | "negative";
  }): Promise<{ ok: boolean }> {
    return this.post(adminApiClient, "/chatbot/feedback", payload);
  }
}

export const adminService = new AdminService();
